import React, { useState, useEffect, useRef } from "react";
import { Route, Redirect, useLocation } from "react-router-dom";

import TopbarComponent from "./Topbar";
import SidebarComponent from "./Sidebar";
import FooterComponent from "./Footer";

import Multilingual from "./../helper/Multilingual";
import SessionExpirationModal from "./../helper/SessionExpirationModal";
import { SESSION_STATUS, ACTIVITY_THROTTLE_MS, getSessionTimeoutState, saveRedirectLocation } from "./../helper/SessionTimeout";

import { ConfigProvider, Layout, Row, Col, Button, Alert, Badge } from "antd";
import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";

import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import md5 from "md5";
import Cookies from "js-cookie";

import "moment/locale/ja";
import "moment/locale/zh-cn";

import { API, DEV_MODE, THEME, SESSION_TIMEOUT, DISPLAY_TEST_ENV_WARNING } from "../config/config";

const { Header, Footer, Sider, Content } = Layout;

let location;

const DEFAULT_COLOR = "#1890ff";

const PrivateRoute = ({ component: Component, ...rest }) => {
    const [collapsed, setCollapsed] = useState(localStorage.getItem("collapsed") === "true" ? true : false);
    // eslint-disable-next-line
    const [theme, setTheme] = useState();

    const [sessionTimeout, setSessionTimeout] = useState({ status: SESSION_STATUS.ACTIVE, deadline: null, inactiveMinutes: 0, expiredReason: null });
    const [isContinuing, setIsContinuing] = useState(false);
    const [continueError, setContinueError] = useState(false);
    // Refs so the long-lived listeners below always see the current values
    const sessionStatusRef = useRef(SESSION_STATUS.ACTIVE);
    const lastActivityRef = useRef(0);
    const usernameRef = useRef(null);
    const [isJwtExpire, setIsJwtExpire] = useState(true); // will change to false after check

    const [t, locale, getDropdownSelectLanguage] = Multilingual();
    // const [theme, setTheme] = useState("#4A63FD");

    const timeoutAfter = SESSION_TIMEOUT && Number.isInteger(SESSION_TIMEOUT) && SESSION_TIMEOUT > 1 ? SESSION_TIMEOUT : 15;

    useEffect(() => {
        if (DEV_MODE) {
            // console.log("process:", process);
        }
    }, [process]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        try {
            usernameRef.current = JSON.parse(localStorage.loggedUser)?.username || null;
        } catch (e) {
            usernameRef.current = null;
        }
        checkJwtExpired();
        dynamicHTMLTitle();
        handleCustomizeTheme();
        if (!DEV_MODE) {
            checkExpirySession();
        }
        checkExpiryPassword();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Idle timeout: wall-clock checks every second, plus immediate re-checks when the tab becomes
    // visible or another tab changes the shared session in localStorage.
    useEffect(() => {
        if (DEV_MODE) {
            return undefined;
        }
        const onActivity = () => {
            // Once the warning is showing, only the Continue button can extend the session
            if (sessionStatusRef.current !== SESSION_STATUS.ACTIVE) return;
            const now = Date.now();
            if (now - lastActivityRef.current < ACTIVITY_THROTTLE_MS) return;
            lastActivityRef.current = now;
            checkExpirySession();
        };
        const onStorage = (e) => {
            if (e.key === null || e.key === "loggedUser" || e.key === "sessionExpired") {
                evaluateSession();
            }
        };
        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                evaluateSession();
            }
        };
        const activityEvents = ["mousedown", "keydown", "wheel", "touchstart", "scroll"];
        const listenerOptions = { capture: true, passive: true };

        activityEvents.forEach((name) => document.addEventListener(name, onActivity, listenerOptions));
        window.addEventListener("storage", onStorage);
        document.addEventListener("visibilitychange", onVisibilityChange);
        const intervalId = setInterval(evaluateSession, 1000);

        return () => {
            activityEvents.forEach((name) => document.removeEventListener(name, onActivity, listenerOptions));
            window.removeEventListener("storage", onStorage);
            document.removeEventListener("visibilitychange", onVisibilityChange);
            clearInterval(intervalId);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const toggle = () => {
        setCollapsed(!collapsed);
        localStorage.setItem("collapsed", !collapsed);
    };

    moment.locale("en");

    location = useLocation();

    const dynamicHTMLTitle = () => {
        const { pathname } = location;
        const keys = pathname.substring(1).split("/");
        document.title = keys[0] ? t(`sidebar.${keys[0]}`) : t(`sidebar.dashboard`);
    };

    const handleCustomizeTheme = () => {
        let vars = {};
        const favicon = document.getElementById("favicon");
        favicon.href = `../themes/${THEME ? THEME : "default"}/favicon.ico`;
        // let vars = { "@primary-color": theme, "@link-color": "@primary-color" };
        if (localStorage.getItem("dashboardtheme")) {
            const dashboardtheme = JSON.parse(localStorage.getItem("dashboardtheme"));
            setTheme(dashboardtheme["@primary-color"]);
            vars = {
                ...vars,
                ...dashboardtheme,
                ...{
                    "@link-color": "@primary-color",
                    "@layout-sider-background": "@primary-color",
                    "@menu-dark-bg": "@primary-color",
                    "@menu-dark-inline-submenu-bg": "@primary-color",
                    "@menu-dark-item-active-bg":  "darken(@primary-color, 5%) !important",
                },
            };
        }
        
        // Remove by Jericho 2023-08-23 - Removing the txshield themes/reseller logo
        // if (localStorage.getItem("logo") && localStorage.getItem("logo") !== "null") {
        //     favicon.href = `data:image/png;base64,${localStorage.getItem("logo")}`;
        // }

        window.less
            .modifyVars(vars)
            .then(() => null)
            .catch((error) => {
                console.error(error);
            });
    };

    const applySessionState = ({ status, deadline, inactiveMinutes }) => {
        if (sessionStatusRef.current === SESSION_STATUS.EXPIRED) return; // expired is final until login
        sessionStatusRef.current = status;
        setSessionTimeout((prev) =>
            prev.status === status && prev.deadline === deadline ? prev : { status, deadline, inactiveMinutes, expiredReason: null }
        );
    };

    const evaluateSession = () => {
        if (sessionStatusRef.current === SESSION_STATUS.EXPIRED) return;

        let loggedUser = null;
        try {
            loggedUser = localStorage.loggedUser ? JSON.parse(localStorage.loggedUser) : null;
        } catch (e) {
            loggedUser = null;
        }
        if (!loggedUser) {
            // Session ended in another tab: a timeout shows the expired notice, a logout follows it to login
            const expiredReason = localStorage.getItem("sessionExpired");
            if (expiredReason) {
                expireSession(expiredReason === "server" ? "server" : "inactivity");
            } else {
                removeSession(true);
            }
            return;
        }

        const state = getSessionTimeoutState(loggedUser.expiry, timeoutAfter);
        if (state.status === SESSION_STATUS.EXPIRED) {
            expireSession("inactivity");
        } else {
            applySessionState(state);
        }
    };

    // End the session in place and keep the expired notice on screen (no automatic redirect).
    const expireSession = (reason) => {
        if (sessionStatusRef.current === SESSION_STATUS.EXPIRED) return;
        sessionStatusRef.current = SESSION_STATUS.EXPIRED;
        saveRedirectLocation(location.pathname + location.search, usernameRef.current);
        // Set the flag (holding the reason) before clearing so other tabs read the removal as a timeout, not a logout
        localStorage.setItem("sessionExpired", reason);
        clearSessionData(["sessionExpired"]);
        setSessionTimeout({ status: SESSION_STATUS.EXPIRED, deadline: null, inactiveMinutes: 0, expiredReason: reason });
    };

    const continueSession = async () => {
        setIsContinuing(true);
        setContinueError(false);
        try {
            // The idle timeout is enforced here; the API token has its own fixed lifetime and no refresh
            // endpoint, so confirm it is still accepted before extending the idle deadline.
            await axios.get(API + "/checksession", {
                headers: { Authorization: "Bearer " + Cookies.get("access_token") },
            });
            evaluateSession(); // the deadline may have passed while waiting
            if (sessionStatusRef.current === SESSION_STATUS.WARNING) {
                updateSession();
            }
        } catch (error) {
            if (error.response && error.response.status === 401) {
                expireSession("server");
            } else {
                setContinueError(true);
            }
        } finally {
            setIsContinuing(false);
        }
    };

    const loginAgain = () => {
        window.location.href = "/login";
    };

    
    const checkExpiryPassword = () =>{

        if (!localStorage.loggedUser || !JSON.parse(localStorage.loggedUser)?.username || !Cookies.get("access_token")) {
            removeSession(true);
            return false;
        }

        //password_changed
        let last_password_days = 90;
        let username = JSON.parse(localStorage.loggedUser)?.username
        let user_password_changed = Cookies.get("password_changed");
        if (user_password_changed) {
            if (user_password_changed === '0000-00-00 00:00:00') {
                Swal.fire({
                    icon: "warning",
                    title: "We require that each user change their password on first logon. Please change your password now.",
                    showConfirmButton: true,
                    onClose: () => {
                        updatePassword(username);
                    },
                });
            }
            if (moment(user_password_changed).diff(moment().subtract(last_password_days, "days")) < 0) {
                Swal.fire({
                    icon: "warning",
                    title: "Your password has expired. Your password is required to be change once every " + last_password_days + " days. Please change your password. Password must have a minimum of 12 characters, must contain both numbers and letters and must not have been used as one of your 4 previous passwords.",
                    showConfirmButton: true,
                    onClose: () => {
                        updatePassword(username);
                    },
                });
            }
        }
    }

    const updatePassword = (values) => {
        axios
            .post(API + "/updatepassword", { "username": values },{withCredentials: true})
            .then(async ({ data }) => {
                if (!data) {
                    Swal.fire({
                        icon: "error",
                        title: t("login.dialogs.login_error"),
                        showConfirmButton: false,
                        timer: 2000,
                    });
                } else  {
                    if(data.status === 'EXC'){
                        Swal.fire({
                            icon: "error",
                            title: data.error_msg,
                            showConfirmButton: false,
                            timer: 2000,
                        });
                    }else{
                        window.open("/resetpassword/"+data.token,'_self');
                    }
                }
            })
            .catch((error) => {
                Swal.fire({
                    icon: "error",
                    title: error.response.data.error_msg,
                    showConfirmButton: false,
                    timer: 2000,
                });
            });
    }

    const checkExpirySession = () => {
        if (sessionStatusRef.current === SESSION_STATUS.EXPIRED) return false;

        if (!localStorage.loggedUser) {
            evaluateSession();
            return false;
        }
        if (!Cookies.get("access_token")) {
            removeSession(true);
            return false;
        }

        const usergroups = Cookies.get("groups");
        if (!usergroups) {
            removeSession();
            return false;
        }

        let loggedUser = JSON.parse(localStorage.loggedUser);
        const {validate, ...loggedUserClone} = loggedUser; // clone without validate
        const expiry = loggedUser.expiry;

        const checkValidate = md5(JSON.stringify(loggedUserClone));
        if (validate !== checkValidate) {
            removeSession();
            return false;
        }
        if (expiry) {
            if (getSessionTimeoutState(expiry, timeoutAfter).status === SESSION_STATUS.ACTIVE) {
                updateSession();
            } else {
                evaluateSession();
            }
        }
    };

    const checkJwtExpired = async () => {
        // if catch error, redirect to login page
        try {
            await axios.get(API + '/checksession', {
                headers: { Authorization: "Bearer " + Cookies.get("access_token") }
            });
            setIsJwtExpire(false);
        } catch (error) {
            await Swal.fire({
                icon: "error",
                title: 'Session expired, please login again.',
                showConfirmButton: true,
                timer: 5000,
            });
            removeSession();
        }
    };
    
    const updateSession = () => {
        if (sessionStatusRef.current === SESSION_STATUS.EXPIRED) return;
        let loggedUser = JSON.parse(localStorage.loggedUser);
        delete loggedUser.validate; // remove validate to avoid mismatch
        loggedUser = { ...loggedUser, expiry: moment().add(timeoutAfter, "minutes").format() };
        loggedUser.validate = md5(JSON.stringify(loggedUser));
        localStorage.setItem("loggedUser", JSON.stringify(loggedUser));
        setContinueError(false);
        applySessionState({ status: SESSION_STATUS.ACTIVE, deadline: null, inactiveMinutes: 0 });
    };

    const removeSession = (rememberLocation = false) => {
        sessionStatusRef.current = SESSION_STATUS.EXPIRED; // stop timeout checks while navigating away
        if (rememberLocation) {
            saveRedirectLocation(location.pathname + location.search, usernameRef.current);
        }
        clearSessionData();
        window.location.href = "/login";
    };

    // keepLocalStorage: keys to retain instead of clearing localStorage entirely
    const clearSessionData = (keepLocalStorage = []) => {
        // Remove by Jericho 2023-08-23 - Clear all localStorage items instead of removing one by one
        // localStorage.removeItem("loggedUser");
        // localStorage.removeItem("access_token");
        // localStorage.removeItem("aggregates");
        // localStorage.removeItem("aggregates_updated");
        // localStorage.removeItem("logo");
        // localStorage.removeItem("dashboardtheme");
        // localStorage.removeItem("cbm_merchant");
        // localStorage.removeItem("authuser");
        // localStorage.removeItem("expiry");
        // localStorage.removeItem("isIdle");
        Cookies.remove("access");
        Cookies.remove("groups");
        Cookies.remove("authuser");
        Cookies.remove("menu_items");
        Cookies.remove("access_token");
        Cookies.remove("associations");
        
        Cookies.remove("userid");
        Cookies.remove("remember_me");
        Cookies.remove("cookie_enabled");
        Cookies.remove("verify_option");
    
        Cookies.remove("oiAssociation");
        Cookies.remove("cbm_token");
        Cookies.remove("threeds_token");
        Cookies.remove("txalert_token");
        Cookies.remove("txfind_token");
        Cookies.remove("oiEnrollments");
        Cookies.remove("dm_token");
        Cookies.remove("dm_userids");

        if (keepLocalStorage.length) {
            Object.keys(localStorage)
                .filter((key) => !keepLocalStorage.includes(key))
                .forEach((key) => localStorage.removeItem(key));
        } else {
            localStorage.clear();
        }
    };

    const getResellerLogo = () => {
        // Remove by Jericho 2023-08-23 - Removing the txshield themes/reseller logo
        // if (localStorage.getItem("logo") && localStorage.getItem("logo") !== "null") {
        //     return <div className="logo logo-reseller" style={{ backgroundImage: `url(data:image/png;base64,${localStorage.getItem("logo")})` }}></div>;
        // }
        return (
            <div
                className="logo"
                style={{ backgroundImage: `url(../themes/${THEME ? THEME : "default"}/${collapsed ? "logo" : "payshield"}-w.png)` }}
            ></div>
        );
    };

    const isSessionExpired = sessionTimeout.status === SESSION_STATUS.EXPIRED;

    return (
        
        <>{ isJwtExpire ? null : // waiting for jwt expire check
        <ConfigProvider locale={locale}>
            <Layout className={locale.locale}>
                <Sider trigger={null} collapsible collapsed={collapsed} width={227} style={{ backgroundColor: theme }}>
                    {/* <Sider trigger={null} collapsible collapsed={collapsed} width={205} style={{ backgroundColor: "white" }}> */}
                    <div style={{padding: '8px 0'}}>
                        {getResellerLogo()}
                    </div>
                    {localStorage.loggedUser && JSON.parse(localStorage.loggedUser)?.username && Cookies.get("access_token") ? <SidebarComponent t={t} theme={theme} pathname={location.pathname} /> : null}
                </Sider>
                <Layout className="site-layout">
                    <Header style={{ padding: 0 }}>
                        <Row type="flex" justify="space-between">
                            <Col>
                                <Button className="trigger-menu" onClick={toggle}>
                                    {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                                </Button>
                            </Col>
                            <Col>
                                {isSessionExpired ? null : <TopbarComponent t={t} changeLanguage={getDropdownSelectLanguage} />}
                            </Col>
                        </Row>
                    </Header>
                    <Content className="content-warp">
                        {DISPLAY_TEST_ENV_WARNING && (
                            <Alert
                                message={
                                    <span>
                                        <Badge count={t(`header.test_label`)} style={{ borderRadius: 2 }} />{" "}
                                        {t(`header.test_message`)}
                                    </span>
                                }
                                type="warning"
                                style={{ marginTop: "10px" }}
                            />
                        )}
                        <Route
                            {...rest}
                            render={(props) =>
                                // Session data is already cleared once expired; hide the page behind the notice
                                isSessionExpired ? null : localStorage.loggedUser && JSON.parse(localStorage.loggedUser)?.username && Cookies.get("access_token") ? (
                                    <Component t={t} theme={theme ? theme : DEFAULT_COLOR} {...props} />
                                ) : (
                                    <Redirect
                                        to={{
                                            pathname: "/login",
                                            state: { from: props.location },
                                        }}
                                    />
                                )
                            }
                        />
                    </Content>
                    <Footer>
                        <FooterComponent />
                    </Footer>
                </Layout>
            </Layout>
            <SessionExpirationModal
                t={t}
                status={sessionTimeout.status}
                deadline={sessionTimeout.deadline}
                inactiveMinutes={sessionTimeout.inactiveMinutes}
                expiredReason={sessionTimeout.expiredReason}
                isContinuing={isContinuing}
                continueError={continueError}
                onContinue={continueSession}
                onLoginAgain={loginAgain}
            />
        </ConfigProvider>
        }</>
    );
};
export default PrivateRoute;
